<template>
    <div id="download-options-modal" ref="modal" tabindex="-1" 
        class="fixed top-0 left-0 right-0 z-[100] hidden w-full p-1 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full bg-black/50 backdrop-blur-sm">
        <div class="relative w-full max-w-md max-h-full">
            <!-- Modal content -->
            <div class="relative bg-white rounded-lg shadow dark:bg-gray-700">
                <!-- Modal header -->
                <div class="flex items-center justify-between p-4 md:p-5 rounded-t dark:border-gray-600 border-b">
                    <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                        Download Options
                    </h3>
                    <button @click="close()" type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
                        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                        <span class="sr-only">Close modal</span>
                    </button>
                </div>
                <!-- Modal body -->
                <div class="p-4 md:p-5 space-y-6">
                    <!-- View Chords Option -->
                    <div class="space-y-3">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            View Chords
                        </label>
                        <div class="flex items-center">
                            <input 
                                type="checkbox" 
                                v-model="options.showChords"
                                class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Show chord diagrams
                            </label>
                        </div>
                    </div>

                    <!-- View Notes Option -->
                    <div class="space-y-3">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            View Notes
                        </label>
                        <div class="flex items-center">
                            <input 
                                type="checkbox" 
                                v-model="options.showNotes"
                                class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Show notes and annotations
                            </label>
                        </div>
                    </div>

                    <!-- View Song Map Option -->
                    <div class="space-y-3">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            View Song Map
                        </label>
                        <div class="flex items-center">
                            <input 
                                type="checkbox" 
                                v-model="options.showSongMap"
                                class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                Show song structure map
                            </label>
                        </div>
                    </div>

                    <!-- Column Layout Option -->
                    <div class="space-y-3">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Column Layout
                        </label>
                        <div class="grid grid-cols-3 gap-2">
                            <button 
                                @click="options.columnLayout = 'column-1'"
                                :class="[
                                    'px-3 py-2 text-sm rounded-md border transition-colors',
                                    options.columnLayout === 'column-1' 
                                        ? 'bg-indigo-600 text-white border-indigo-600' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500'
                                ]"
                            >
                                1 Column
                            </button>
                            <button 
                                @click="options.columnLayout = 'column-2'"
                                :class="[
                                    'px-3 py-2 text-sm rounded-md border transition-colors',
                                    options.columnLayout === 'column-2' 
                                        ? 'bg-indigo-600 text-white border-indigo-600' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500'
                                ]"
                            >
                                2 Columns
                            </button>
                            <button 
                                @click="options.columnLayout = 'column-3'"
                                :class="[
                                    'px-3 py-2 text-sm rounded-md border transition-colors',
                                    options.columnLayout === 'column-3' 
                                        ? 'bg-indigo-600 text-white border-indigo-600' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500'
                                ]"
                            >
                                3 Columns
                            </button>
                        </div>
                    </div>

                    <!-- Orientation Option -->
                    <div class="space-y-3">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Orientation
                        </label>
                        <div class="grid grid-cols-2 gap-2">
                            <button 
                                @click="options.orientation = 'portrait'"
                                :class="[
                                    'px-3 py-2 text-sm rounded-md border transition-colors',
                                    options.orientation === 'portrait' 
                                        ? 'bg-indigo-600 text-white border-indigo-600' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500'
                                ]"
                            >
                                Portrait
                            </button>
                            <button 
                                @click="options.orientation = 'landscape'"
                                :class="[
                                    'px-3 py-2 text-sm rounded-md border transition-colors',
                                    options.orientation === 'landscape' 
                                        ? 'bg-indigo-600 text-white border-indigo-600' 
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500'
                                ]"
                            >
                                Landscape
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Modal footer -->
                <div class="flex items-center justify-end p-4 md:p-5 border-t border-gray-200 dark:border-gray-600 space-x-2">
                    <button 
                        @click="close()" 
                        type="button" 
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-600 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700"
                    >
                        Cancel
                    </button>
                    <button 
                        @click="confirmDownload()" 
                        type="button" 
                        class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Download
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, defineExpose, onMounted, ref } from "vue";
import { Modal } from "flowbite";

const DOWNLOAD_OPTIONS_STORAGE_KEY = 'triad-download-options';

const props = defineProps({
    currentColumnLayout: {
        type: String,
        default: 'column-1'
    },
    currentShowChords: {
        type: Boolean,
        default: true
    },
    currentShowNotes: {
        type: Boolean,
        default: true
    },
    currentShowSongMap: {
        type: Boolean,
        default: true
    }
});

const emits = defineEmits(['download']);

const modal = ref(null);
let modalElement = ref(null);

const loadSavedOptions = () => {
    try {
        const saved = localStorage.getItem(DOWNLOAD_OPTIONS_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                showChords: parsed.showChords !== undefined ? parsed.showChords : props.currentShowChords,
                showNotes: parsed.showNotes !== undefined ? parsed.showNotes : props.currentShowNotes,
                showSongMap: parsed.showSongMap !== undefined ? parsed.showSongMap : props.currentShowSongMap,
                columnLayout: parsed.columnLayout || props.currentColumnLayout,
                orientation: parsed.orientation || 'portrait'
            };
        }
    } catch (e) {
        console.error('Error loading download options:', e);
    }
    return {
        showChords: props.currentShowChords,
        showNotes: props.currentShowNotes,
        showSongMap: props.currentShowSongMap,
        columnLayout: props.currentColumnLayout,
        orientation: 'portrait'
    };
};

const saveOptions = (opts) => {
    try {
        localStorage.setItem(DOWNLOAD_OPTIONS_STORAGE_KEY, JSON.stringify(opts));
    } catch (e) {
        console.error('Error saving download options:', e);
    }
};

// Options loaded from storage or default props
const options = ref(loadSavedOptions());

onMounted(() => {
    modalElement.value = new Modal(modal.value, {
        placement: 'enter', 
        backdrop: 'static', 
        backdropClasses: "bg-black/50 backdrop-blur-sm z-[90]"
    });
});

const open = () => {
    options.value = loadSavedOptions();
    modalElement.value.show();
};

const close = () => {
    modalElement.value.hide();
};

const confirmDownload = () => {
    saveOptions(options.value);
    emits('download', options.value);
    close();
};

defineExpose({ open, close });
</script> 